import { API, graphqlOperation } from "aws-amplify";
import { ResourceType, UNSPOTTED_TRACK_ID } from "../../utils/constants";
import {
  updateInboundShipment,
  updateInboundShipmentMini,
} from "./custom-mutations";

// performDeleteAccount - call graphQl, no try-catch
// handleCreateAccount - with try-catch
// processUpdateAccount - process for lambda

export const sanitizeInboundLambda = ({
  account,
  area,
  billToAccount,
  carrier, // @connection object — keep carrierId/carrierName scalars
  commodity,
  driver, // @connection object — keep driverId/driverName scalars
  inventories,
  invoice,
  invoicedServices,
  inValidSealNumber,
  inValidSeals,
  location,
  manualCharges,
  photos,
  railEquipmentType,
  reloadYardCommodity,
  seals, // do not remove sealNumbers array
  validSeals,
  timeslot,
  timeslotId, // schedule link — processInbound Lambda owns this field
  totalQuantity, // dynamic calc side-effect
  trackId, // shipment-root — omit from mutation input; canonical placement is trackCar.trackId
  trackCar,
  truckEquipmentType,
  zone,
  ...rest
} = {}) => rest;

export const sanitizeInbound = (inbound) => {
  const sanitized = sanitizeInboundLambda(inbound);
  const { createdAt, updatedAt, ...rest } = sanitized;
  return rest;
};

/**
 * Sanitize for the record handed back to react-admin's cache (NOT mutation
 * input): strip @connection objects like sanitizeInbound, but keep the
 * schedule link. lambdaSaveInbound branches on record.timeslotId — dropping
 * it from the cached record sends the next save down the create-appointment
 * path and fails the processInbound Lambda's attribute_not_exists(timeslotId)
 * transaction condition.
 */
export const sanitizeInboundRecord = (inbound) => {
  const record = sanitizeInbound(inbound);
  if (inbound?.timeslotId !== undefined) record.timeslotId = inbound.timeslotId;
  if (inbound?.timeslot !== undefined) record.timeslot = inbound.timeslot;
  return record;
};

/**
 * Build the narrow inbound header payload that must stay in sync with timeslot
 * writes done inside the processInbound Lambda transaction. Pass null as
 * timeslotId to clear an existing appointment.
 *
 * `expectedStatus` is optional and conditions the write on the shipment still
 * being at that status — the carrier dispatch save passes it so booking a
 * delivery cannot rebuild the sort key below around a status the yard has since
 * advanced. The manager saves change status themselves and must not condition
 * on the value they are replacing, so they leave it off.
 *
 * `expectedCurrentScheduledDateTime` is optional in the same way, and conditions
 * the write on the appointment not having been moved since it was read. It is
 * not redundant with `expectedCurrentTimeslotId`: rescheduling reuses the
 * timeslot row, so the link reads the same either side of a move.
 *
 * `historyAppendList` is appended by the Lambda rather than replacing the
 * stored history, so a note added while the form was open survives the write.
 */
export const buildInboundScheduleUpdateInput = (
  inbound,
  {
    timeslotId = undefined,
    expectedCurrentTimeslotId = undefined,
    expectedCurrentScheduledDateTime = undefined,
    expectedStatus = undefined,
    historyAppendList = [],
  } = {},
) => {
  const sanitized = sanitizeInboundLambda(inbound);
  if (!sanitized?.id) return null;

  if (timeslotId === undefined) {
    throw new Error(
      "buildInboundScheduleUpdateInput requires timeslotId. Pass null to clear an existing appointment.",
    );
  }

  const input = {
    id: sanitized.id,
    // Index-key inputs (read, not written): processInbound rebuilds
    // "status#releaseDateTime#placedDateTime#notifyDateTime#scheduledDateTime#createDateTime"
    // from these. "state#railCarNumber" is deliberately left alone — it holds no
    // scheduledDateTime, so an appointment cannot move it, and rewriting it from
    // this snapshot would revert a yard edit to either component.
    status: sanitized.status,
    // Carried for the deployed lambda only, which SETs railCarNumber and state
    // and rebuilds "state#railCarNumber" from them. The schedule handler this
    // payload targets reads neither, so sending them keeps a frontend that
    // ships ahead of `amplify push` working instead of failing every save: the
    // old lambda would otherwise marshal undefined into its expression values
    // and reject the whole appointment transaction. Drop both once the backend
    // is deployed everywhere.
    state: sanitized.state,
    railCarNumber: sanitized.railCarNumber || "",
    notifyDateTime: sanitized.notifyDateTime || null,
    placedDateTime: sanitized.placedDateTime || null,
    releaseDateTime: sanitized.releaseDateTime || null,
    scheduledDateTime: sanitized.scheduledDateTime || null,
    createDateTime: sanitized.createDateTime,
    timeslotId,
    historyAppendList,
    // Lambda owns this write; never echo the form's loaded updatedAt (stale vs stream/subscribers).
    updatedAt: new Date().toISOString(),
    expectedCurrentTimeslotId,
  };

  // The appointment time the caller READ, not the one it is writing —
  // input.scheduledDateTime above is the new value.
  if (expectedCurrentScheduledDateTime !== undefined)
    input.expectedCurrentScheduledDateTime = expectedCurrentScheduledDateTime;

  if (expectedStatus !== undefined) input.expectedStatus = expectedStatus;

  return input;
};

/** Build TrackCar create input (single shape for lambda and GraphQL create). */
export const buildTrackCarCreateInput = (inbound) => {
  if (!inbound?.id) return null;
  const createdAt = inbound.createdAt || new Date().toISOString();
  return {
    id: inbound.id,
    __typename: "TrackCar",
    owner: inbound.owner,
    active: "true",
    reloadYardId: inbound.reloadYardId,
    accountId: inbound.accountId,
    shipmentId: inbound.id,
    parentType: ResourceType.INBOUND,
    railCarNumber: inbound.railCarNumber || "",
    trackId: UNSPOTTED_TRACK_ID,
    eodTrackId: UNSPOTTED_TRACK_ID,
    eodSortKey: "",
    sortKey: "",
    createdAt,
    updatedAt: createdAt,
  };
};

/** Build TrackCar update input for rail-to-rail shipment edits. */
export const buildTrackCarUpdateInput = (inbound) => {
  if (!inbound?.id) return null;
  return {
    id: inbound.id,
    railCarNumber: inbound.railCarNumber || "",
    updatedAt: new Date().toISOString(),
  };
};

// custom-mutations.updateInboundShipment
export const performUpdateInbound = async (inbound) => {
  const inboundInput = sanitizeInbound(inbound);
  return API.graphql(
    graphqlOperation(updateInboundShipment, { input: inboundInput }),
  );
};

// custom-mutations.updateInboundShipmentMini
export const performUpdateInboundMini = async (inbound) => {
  const inboundInput = sanitizeInbound(inbound);
  return API.graphql(
    graphqlOperation(updateInboundShipmentMini, { input: inboundInput }),
  );
};
